// ══════════════════════════════════════════════════════════
// LIVE SESSION SERVICE — Backend PHP Contract
//
// Inicia, consulta y finaliza sesiones live contra el backend PHP.
// Estos endpoints manejan el ciclo de vida completo de una
// transmisión: arranque del encoder, verificación de stream,
// health checks, y teardown graceful al finalizar.
//
// NO arranca streams desde el navegador.
// NO consulta CentovaCast / AzuraCast directamente.
// NO expone credenciales ni rutas administrativas.
//
// Endpoints:
//   POST   /api/v1/live/start
//   GET    /api/v1/live/sessions/{sessionId}
//   POST   /api/v1/live/sessions/{sessionId}/finish
//
// Envelope estándar del backend:
//   { success, message, data, errors, meta }
//
// La sesión SIEMPRE se lee de response.data.session.
// ══════════════════════════════════════════════════════════

import { backendApiRequest, RfwBackendError } from '@/services/rfwBackend';
import type {
  LiveSession,
  StartLiveSessionPayload,
  StartLiveSessionData,
  GetLiveSessionData,
  FinishLiveSessionData,
} from '@/types/liveSession';

// ── ERROR MAPPER ──

/**
 * Traduce errores HTTP a mensajes amigables.
 * Nunca expone respuestas crudas del proveedor ni rutas internas.
 */
function mapLiveSessionError(err: unknown): never {
  if (err instanceof RfwBackendError) {
    switch (err.statusCode) {
      case 401:
        throw new Error('Tu sesión expiró. Volvé a iniciar sesión para controlar la transmisión.');
      case 403:
        throw new Error('No tenés permisos para controlar transmisiones en esta estación.');
      case 404:
        throw new Error('Sesión live no encontrada. Verificá que exista.');
      case 409:
        throw new Error(
          'Ya hay una sesión live activa para esta estación. Finalizala antes de iniciar una nueva.',
        );
      case 422:
        throw new Error('Datos inválidos. Verificá que la estación y la radio estén configuradas.');
      case 429:
        throw new Error('Demasiadas solicitudes. Esperá unos segundos antes de intentar de nuevo.');
      default:
        if (err.statusCode >= 500) {
          throw new Error(
            'El servicio de transmisión encontró un error interno. Revisá el estado del servidor e intentá de nuevo.',
          );
        }
        throw new Error(err.message || 'Error al operar la sesión live.');
    }
  }
  throw err instanceof Error ? err : new Error('Error desconocido al operar la sesión live.');
}

// ── API METHODS ──

/**
 * Inicia una nueva sesión live.
 * POST /api/v1/live/start
 *
 * El backend PHP se encarga de:
 *   1. Validar que la estación existe y está configurada.
 *   2. Iniciar el encoder / source en CentovaCast.
 *   3. Verificar que el stream esté publicándose.
 *   4. Activar health checks periódicos.
 *   5. Devolver la sesión con estado inicial.
 *
 * Body: { station_id, radio_id, program_id? }
 *
 * Respuesta: data = { session, polling, autodj, next_step }
 * Retorna exclusivamente data.session.
 */
export async function startLiveSession(
  payload: StartLiveSessionPayload,
): Promise<LiveSession> {
  try {
    const response = await backendApiRequest<StartLiveSessionData>(
      '/api/v1/live/start',
      {
        method: 'POST',
        body: payload as unknown as Record<string, unknown>,
      },
    );
    return response.data.session;
  } catch (err: unknown) {
    mapLiveSessionError(err);
    throw err;
  }
}

/**
 * Consulta el estado actual de una sesión live.
 * GET /api/v1/live/sessions/{sessionId}
 *
 * Devuelve la sesión completa con su estado actual, métricas
 * de salud, contador de desconexiones y último heartbeat.
 *
 * Usar para polling durante la transmisión y para verificar
 * el estado final después de finishLiveSession.
 *
 * Respuesta: data = { session }
 * Retorna exclusivamente data.session.
 */
export async function getLiveSession(
  sessionId: string,
): Promise<LiveSession> {
  try {
    const response = await backendApiRequest<GetLiveSessionData>(
      `/api/v1/live/sessions/${encodeURIComponent(sessionId)}`,
      { method: 'GET' },
    );
    return response.data.session;
  } catch (err: unknown) {
    mapLiveSessionError(err);
    throw err;
  }
}

/**
 * Finaliza una sesión live activa.
 * POST /api/v1/live/sessions/{sessionId}/finish
 *
 * El backend PHP se encarga de:
 *   1. Cambiar el estado a 'ending'.
 *   2. Detener health checks.
 *   3. Desconectar el encoder / source en CentovaCast.
 *   4. Guardar métricas finales (duración, desconexiones).
 *   5. Cambiar el estado a 'completed' (o 'failed' si algo falló).
 *
 * ⚠️ Requiere confirmación del usuario antes de llamar.
 *
 * Respuesta: data = { session, finish, heartbeat, autodj_monitor, next_step }
 * Retorna exclusivamente data.session.
 */
export async function finishLiveSession(
  sessionId: string,
): Promise<LiveSession> {
  try {
    const response = await backendApiRequest<FinishLiveSessionData>(
      `/api/v1/live/sessions/${encodeURIComponent(sessionId)}/finish`,
      { method: 'POST' },
    );
    return response.data.session;
  } catch (err: unknown) {
    mapLiveSessionError(err);
    throw err;
  }
}

// ── EXPORT AGRUPADO ──

export const liveSessionService = {
  startLiveSession,
  getLiveSession,
  finishLiveSession,
};